import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE_NAME = 'Kehele Keff Music';
const SITE_DESC = 'Professional DJ edits made affordable — buy exclusive edits, VIP packs, DJ tools and pop hits from Kehele Keff.';
const SITE_URL = typeof window !== 'undefined' ? window.location.origin : '';

/**
 * ReleaseSEO — injects per-page metadata for a release detail page.
 *
 * Emits: <title>, meta description, canonical, OG tags, Twitter Card, and a
 * Schema.org Product JSON-LD block. Everything is safe if `album` is null (renders
 * nothing) so the caller can render it inline while the album is still loading.
 */
export const ReleaseSEO = ({ album, currentPath }) => {
  if (!album) return null;

  const title = album.title || 'Release';
  const artist = album.artist || SITE_NAME;

  // Build a rich, keyword-heavy description from every hint the DJ tagged.
  const trackTags = new Set();
  (album.tracks || []).forEach((t) => {
    if (t?.bpm) trackTags.add(`${t.bpm} BPM`);
    if (t?.key) trackTags.add(`Key ${String(t.key).toUpperCase()}`);
    (t?.version_tags || []).forEach((v) => v && trackTags.add(v));
  });
  const releaseTypeLabel = ({
    'exclusive': 'exclusive DJ edit',
    'vip-packs': 'VIP mashup pack',
    'dj-intro': 'DJ intro',
    'pop-hits': 'pop hit remix',
    'dj-tools': 'DJ tool',
    'free-download': 'free DJ edit',
  }[album.release_type] || 'DJ edit');

  const descBits = [
    `${title} by ${artist} — ${releaseTypeLabel} available on ${SITE_NAME}.`,
    trackTags.size ? Array.from(trackTags).slice(0, 8).join(' · ') : null,
    album.description ? String(album.description).replace(/\s+/g, ' ').slice(0, 140) : null,
  ].filter(Boolean);
  const description = descBits.join(' ').slice(0, 300);

  const slug = album.slug || album.id;
  const canonical = SITE_URL ? `${SITE_URL}/release/${slug}` : `/release/${slug}`;
  const cover = album.cover_url || `${SITE_URL}/kehele-keff-logo.webp`;

  const structured = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: title,
    image: [cover],
    description,
    brand: { '@type': 'Brand', name: artist || SITE_NAME },
    sku: album.id,
    url: canonical,
    offers: {
      '@type': 'Offer',
      priceCurrency: 'PHP',
      price: String(Math.max(0, Number(album.price || 0))),
      availability: 'https://schema.org/InStock',
      url: canonical,
      seller: { '@type': 'Organization', name: SITE_NAME },
    },
  };

  const pageTitle = `${title} — ${artist} | ${SITE_NAME}`;

  return (
    <Helmet>
      <title>{pageTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />

      <meta property="og:type" content="product" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={cover} />
      <meta property="og:url" content={canonical} />
      <meta property="product:price:amount" content={String(album.price || 0)} />
      <meta property="product:price:currency" content="PHP" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={cover} />

      <script type="application/ld+json">{JSON.stringify(structured)}</script>
    </Helmet>
  );
};

/**
 * PageSEO — generic per-page helmet for non-product pages (about, contact, legal…).
 */
export const PageSEO = ({ title, description, path }) => {
  const canonical = SITE_URL ? `${SITE_URL}${path || ''}` : (path || '');
  const desc = description || SITE_DESC;
  const pageTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} — Professional DJ Edits`;
  return (
    <Helmet>
      <title>{pageTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={canonical} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={canonical} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={desc} />
    </Helmet>
  );
};

export default ReleaseSEO;
